import { Select } from "@/components/ui/select";
import type { BatchCharacteristicResponse } from "@/types/batch-characteristics.types";
import type {
  ImportCharacteristicColumn,
  ImportColumnMapping,
  ValueTranslation,
} from "@/types/data-import.types";
import { ValueTranslationTable } from "./ValueTranslationTable";

interface CharacteristicColumnRowProps {
  column: string;
  decision: Extract<ImportColumnMapping, { action: "CHARACTERISTIC" }>;
  characteristics: BatchCharacteristicResponse[];
  /** A coluna já contada pela pré-visualização. Ausente enquanto ela não chegou. */
  preview: ImportCharacteristicColumn | undefined;
  onChange: (decision: ImportColumnMapping) => void;
}

/**
 * Spec 084 (FR-018) — a coluna que descreve a BATELADA em vez de uma quantidade.
 *
 * A escolha da característica vem primeiro; só depois o de-para faz sentido, porque são os valores
 * DELA que a tabela oferece. Trocar a característica zera as traduções: um valor da anterior não
 * existe na nova, e mantê-lo gravaria na batelada um rótulo de outra coisa.
 */
export function CharacteristicColumnRow({
  column,
  decision,
  characteristics,
  preview,
  onChange,
}: CharacteristicColumnRowProps) {
  const def = characteristics.find((c) => c.id === decision.characteristicId);

  function setTranslations(translations: Record<string, ValueTranslation>) {
    onChange({ ...decision, translations });
  }

  return (
    <div className="space-y-2">
      <Select
        aria-label={`Característica de batelada da coluna ${column}`}
        value={decision.characteristicId}
        onChange={(e) =>
          onChange({
            action: "CHARACTERISTIC",
            characteristicId: e.target.value,
            translations: {},
          })
        }
      >
        <option value="">Escolha a característica</option>
        {characteristics.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </Select>

      {def ? (
        <p className="text-[12px] text-text-secondary">
          O valor é gravado nas linhas de marcação de &quot;{def.name}&quot;. Nas etapas seguintes
          da cadeia ele chega por herança.
        </p>
      ) : null}

      {def && preview ? (
        <ValueTranslationTable
          column={preview}
          options={def.values.map((v) => ({ id: v.id, label: v.label }))}
          translations={decision.translations}
          onChange={setTranslations}
        />
      ) : def ? (
        <p className="text-[12px] text-text-tertiary">
          Salve o mapeamento para ver as grafias que este arquivo traz.
        </p>
      ) : null}
    </div>
  );
}
